import axios from 'axios';
import FileDownload from 'js-file-download';

import { OrderByTypeEnum } from '../components/organisms/FinancialTable/FinancialTable';
import { getDocuments } from '../services/documents';
import { webSocketApiUrlsDictionary } from '../constants';

type Headers = {
  Authorization?: string;
  Platform: string;
};

const defaultHeaders: Headers = {
  Platform: 'DASHBOARD',
};

const token = localStorage.getItem('auth-token');

if (token) {
  defaultHeaders.Authorization = `Bearer ${token}`;
}

const theme = localStorage.getItem('theme');

const defaultUrl =
  webSocketApiUrlsDictionary[theme]?.gatewayUrl ||
  process.env.REACT_APP_API_GATEWAY_URL;

const BASE_URL = `${defaultUrl}/mk-ms-financial`;
const BASE_URL_PB = `${defaultUrl}/mk-ms-payment-pagbank`;

const getLocalStorages = () => {
  const tenantApiKey = localStorage.getItem('api-key');

  return { tenantApiKey };
};

const offsetInMinutes = new Date().getTimezoneOffset();

const timezoneOffset = -(offsetInMinutes / 60);

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    ...defaultHeaders,
    'X-Timezone-Offset': timezoneOffset,
  },
});

export const apiPB = axios.create({
  baseURL: BASE_URL_PB,
  headers: {
    ...defaultHeaders,
    'X-Timezone-Offset': timezoneOffset,
  },
});

export const getSummary = (
  page: number = 1,
  size: number = 10,
  startDate?: string,
  endDate?: string,
  transactionPk?: string,
  orderBy?: string,
  tenantId?: string,
  chargeBoxId?: string,
  paymentStatus?: string,
) => {
  const { tenantApiKey } = getLocalStorages();

  const sort =
    orderBy === OrderByTypeEnum.FINANCIAL_MOVEMENT
      ? 'paidTimestamp,ASC'
      : 'createdTimestamp,ASC';

  try {
    const params = {
      page: --page,
      size,
      startDate,
      endDate,
      userEmailOrTransactionId: transactionPk || null,
      sort,
      chargeBoxId: chargeBoxId || null,
      tenantApiKey: tenantId || tenantApiKey,
      searchTenantChildren: true,
      paymentStatus: paymentStatus || null,
    };

    return api.get('/transactions/summary', { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getTransactions = async (
  targetType: 'allMovements' | 'deposits' | 'recharges',
  page: number = 1,
  size: number = 10,
  startDate?: string,
  endDate?: string,
  transactionPk?: string,
  orderBy?: string,
  tenantId?: string,
  chargeBoxId?: string,
  paymentStatus?: string,
) => {
  const { tenantApiKey } = getLocalStorages();

  const sort =
    orderBy === OrderByTypeEnum.FINANCIAL_MOVEMENT
      ? 'paidTimestamp,DESC'
      : 'createdTimestamp,DESC';

  try {
    const params = {
      page: --page,
      size,
      startDate,
      endDate,
      userEmailOrTransactionId: transactionPk || null,
      sort,
      chargeBoxId: chargeBoxId || null,
      tenantApiKey: tenantId || tenantApiKey,
      searchTenantChildren: true,
      paymentStatus: paymentStatus || null,
    };

    const tab = targetType !== 'allMovements' ? `/${targetType}` : '';

    const apiUrl = `/transactions${tab}`;

    return api.get(apiUrl, { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const downloadTransactions = async (
  targetType: 'allMovements' | 'deposits' | 'recharges',
  startDate?: string,
  endDate?: string,
  transactionPk?: string,
  orderBy?: string,
  tenantId?: string,
  chargeBoxId?: string,
  paymentStatus?: string,
  shouldShowBeneficiaryAccounts?: boolean,
) => {
  const { tenantApiKey } = getLocalStorages();

  try {
    const sort =
      orderBy === OrderByTypeEnum.FINANCIAL_MOVEMENT
        ? 'paidTimestamp,DESC'
        : 'createdTimestamp,DESC';

    const params = {
      startDate,
      endDate,
      userEmailOrTransactionId: transactionPk || null,
      sort,
      chargeBoxId: chargeBoxId || null,
      paymentStatus: paymentStatus || null,
      tenantApiKey: tenantId || tenantApiKey,
      searchTenantChildren: true,
      shouldShowBeneficiaryAccounts: shouldShowBeneficiaryAccounts || null,
    };

    const tab = targetType !== 'allMovements' ? `/${targetType}` : '';

    const apiUrl = `/transactions${tab}`;

    const response: any = await api.get(apiUrl, {
      params,
      headers: {
        accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      responseType: 'blob',
    });

    FileDownload(response.data, 'relatorio-movimentacoes-financeiras.xlsx');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const sendRefund = async (
  transactionPk: number,
  partialAmount?: number,
) => {
  try {
    let url = `/transactions/${transactionPk}/payments/refunds`;

    if (partialAmount) {
      url = url.concat(`?partialAmount=${partialAmount}`);
    }

    await api.post(url);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const nfeResend = async (transactionPk: number) => {
  try {
    const url = `/transactions/${transactionPk}/nfes/resends`;

    await api.post(url);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getTenants = () => {
  const { tenantApiKey } = getLocalStorages();

  try {
    const params = {
      tenantApiKey,
      searchTenantChildren: true,
    };

    return api.get('/tenants', { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getExternalChargeId = async (paymentExternalId: string) => {
  try {
    const response = await apiPB.get(`/payments/chargeId/${paymentExternalId}`);

    return response.data.replace(/^.*?_/, '');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getExternalReferenceId = async (
  transactionReferenceId: number,
  userUuid: string,
) => {
  try {
    const response = await apiPB.get(
      `/payments/externalReferenceId/${transactionReferenceId}`,
      { params: { userUuid } },
    );

    return response.data.map((id: string) => id.replace(/^.*?_/, ''));
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getWalletManualAdjustmentOperator = async (
  userWalletPaymentPk: number,
) => {
  const data = await getDocuments(
    `customer-wallet/deposits/${userWalletPaymentPk}/manual-adjustment-operator`,
  );
  return data && typeof data === 'object' ? data : null;
};

export const getUserUuid = async (userPk: number): Promise<string> => {
  try {
    const request: any = getDocuments(`users_data/${userPk}`);
    const response = await request;
    const uuid: string | undefined =
      response?.userForm?.uuid ?? response?.form?.uuid ?? response?.uuid;

    if (!uuid) {
      throw new Error('UUID not present in users_data response');
    }

    return uuid;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getChargerboxes = () => {
  const { tenantApiKey } = getLocalStorages();

  try {
    const params = {
      tenantApiKey,
      searchTenantChildren: true,
    };

    return api.get('/chargeboxes', { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};
